import {
  decodeFromRadio,
  createWantConfigRequest,
  createHeartbeat,
  DecodedFromRadio,
  DecodedMeshPacket,
  DecodedNodeInfo,
  DecodedMyNodeInfo
} from '../protocol/codec';
import { RadioTransport, ConnectionState, TransportType } from './types';
import { SerialTransport } from './serialTransport';
import { BleTransport } from './bleTransport';

export interface RadioClientEvents {
  stateChange: (state: ConnectionState, transportName?: string) => void;
  packet: (packet: DecodedMeshPacket) => void;
  nodeInfo: (node: DecodedNodeInfo) => void;
  myInfo: (myInfo: DecodedMyNodeInfo) => void;
  syncProgress: (count: number, complete: boolean) => void;
  error: (error: Error) => void;
}

/**
 * Nonce 69421 is SPECIAL_NONCE_ONLY_NODES in the Meshtastic firmware.
 * It tells the radio to skip configs/channels and only stream NodeDB entries.
 */
const NODEDB_ONLY_NONCE = 69421;

/**
 * Any other nonce triggers a FULL config dump (myInfo → config → channels
 * → modules → files → nodeDB → configComplete). We use 69420 by convention.
 */
const FULL_CONFIG_NONCE = 69420;

/**
 * Unified radio client managing hardware transports, automatic NodeDB synchronization,
 * packet decoding, and reconnection logic.
 *
 * Handshake protocol (firmware PhoneAPI.cpp):
 *   wantConfigId with nonce 69420 (SPECIAL_NONCE_ONLY_CONFIG) → Stage 1: radio sends configs/channels/files + configCompleteId(69420).
 *   Heartbeat settle delay (~100ms).
 *   wantConfigId with nonce 69421 (SPECIAL_NONCE_ONLY_NODES) → Stage 2: radio streams NodeDB + configCompleteId(69421).
 */
export class RadioClient {
  private transport: RadioTransport | null = null;
  private currentTransportType: TransportType | null = null;
  private state: ConnectionState = 'DISCONNECTED';

  private myNodeInfo: DecodedMyNodeInfo | null = null;
  private syncedNodesCount = 0;
  private configNonce = 0;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private heartbeatNonce = 1;

  private syncWatchdogTimer: ReturnType<typeof setTimeout> | null = null;
  private syncRetryCount = 0;
  private connectionReadyTimestamp = 0;
  private settleUntilTimestamp = 0;

  private isExplicitDisconnect = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 8;
  private baseReconnectDelayMs = 2000;
  private maxReconnectDelayMs = 30000;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  private listeners: { [K in keyof RadioClientEvents]?: RadioClientEvents[K][] } = {};

  constructor() {
    this.handlePacketReceived = this.handlePacketReceived.bind(this);
    this.handleTransportDisconnect = this.handleTransportDisconnect.bind(this);
    this.handleTransportError = this.handleTransportError.bind(this);
  }

  /**
   * Connects to a Meshtastic radio via chosen transport (serial or ble).
   *
   * Why: Initializes the physical/GATT transport, registers packet hooks, starts keepalive
   * heartbeats, and begins the canonical two-stage handshake.
   *
   * @param type - Transport method ('serial' or 'ble')
   */
  async connect(type: TransportType): Promise<void> {
    this.cancelReconnectTimer();
    this.isExplicitDisconnect = false;
    this.reconnectAttempts = 0;

    if (this.transport && this.transport.isConnected()) {
      await this.disconnect();
      this.isExplicitDisconnect = false;
    }

    this.currentTransportType = type;
    this.connectionReadyTimestamp = 0;
    this.settleUntilTimestamp = Infinity;
    this.setState('CONNECTING');

    try {
      this.transport = type === 'serial' ? new SerialTransport() : new BleTransport();
      this.transport.onPacket(this.handlePacketReceived);
      this.transport.onError(this.handleTransportError);
      this.transport.onDisconnect(this.handleTransportDisconnect);

      await this.transport.connect();

      this.setState('SYNCING_NODEDB');
      this.syncedNodesCount = 0;
      this.syncRetryCount = 0;
      this.startHeartbeatLoop();
      this.resetSyncWatchdog();

      // Small delay to let BLE drain stale data and let serial resync bytes propagate
      await new Promise((r) => setTimeout(r, 200));

      // Request full config dump (Stage 1: nonce 69420)
      await this.requestFullConfig();
    } catch (err) {
      this.stopSyncWatchdog();
      this.setState('ERROR');
      const error = err instanceof Error ? err : new Error(String(err));
      this.emit('error', error);
      throw error;
    }
  }

  /**
   * Sends wantConfigId with FULL_CONFIG_NONCE (69420) to request Stage 1 config from the radio.
   *
   * Why: Meshtastic PhoneAPI two-stage handshake requires Stage 1 (69420) to stream
   * node configuration, channel definitions, and metadata prior to the NodeDB stream.
   */
  private async requestFullConfig(): Promise<void> {
    this.configNonce = FULL_CONFIG_NONCE;
    console.info(`[RadioClient] Sending wantConfigId=${this.configNonce} (full config + NodeDB)...`);
    try {
      const req = createWantConfigRequest(this.configNonce);
      await this.send(req);
      console.info('[RadioClient] wantConfigId sent successfully');
    } catch (err) {
      console.error('[RadioClient] Failed to send wantConfigId:', err);
    }
  }

  /**
   * Requests only the NodeDB (skipping configs/channels/modules).
   * Uses the special nonce 69421 (SPECIAL_NONCE_ONLY_NODES in firmware).
   *
   * Why: Meshtastic firmware supports fast-path NodeDB synchronization skipping channel
   * definitions. Used as Stage 2 of the two-stage connection handshake and for manual sync triggers.
   */
  async requestNodeDbSync(): Promise<void> {
    if (!this.transport || !this.transport.isConnected()) return;
    this.configNonce = NODEDB_ONLY_NONCE;
    this.syncedNodesCount = 0;
    this.connectionReadyTimestamp = 0;
    this.settleUntilTimestamp = Infinity;
    this.setState('SYNCING_NODEDB');
    this.resetSyncWatchdog();
    console.info(`[RadioClient] Sending wantConfigId=${this.configNonce} (NodeDB only)...`);
    try {
      // Send heartbeat first to reset idle timer, then request NodeDB
      await this.send(createHeartbeat(this.heartbeatNonce++));
      await new Promise((r) => setTimeout(r, 100));

      const req = createWantConfigRequest(this.configNonce);
      await this.send(req);
      console.info('[RadioClient] NodeDB-only wantConfigId sent successfully');
    } catch (err) {
      console.error('[RadioClient] Failed to request NodeDB sync:', err);
    }
  }

  /**
   * Disconnects current radio transport and resets local sync counters.
   *
   * Why: Ensures background timers, watchdogs, and transport handles are cleared cleanly.
   */
  async disconnect(): Promise<void> {
    this.cancelReconnectTimer();
    this.isExplicitDisconnect = true;
    this.reconnectAttempts = 0;
    this.stopHeartbeatLoop();
    this.stopSyncWatchdog();

    if (this.transport) {
      try {
        await this.transport.disconnect();
      } catch (err) {
        console.warn('[RadioClient] Error during transport disconnect:', err);
      }
      this.transport = null;
    }
    this.setState('DISCONNECTED');
  }

  /**
   * Transmits raw Protobuf bytes over the active transport.
   *
   * Why: Central egress point for all ToRadio protobuf messages (packets, heartbeats, wants).
   *
   * @param payload - Serialized ToRadio Protobuf bytes
   */
  async send(payload: Uint8Array): Promise<void> {
    if (!this.transport || !this.transport.isConnected()) {
      throw new Error('No radio transport connected.');
    }
    await this.transport.send(payload);
  }

  /**
   * Starts or resets the NodeDB synchronization watchdog timer.
   *
   * Why: If a radio device or transport stack drops a wantConfigId handshake request or
   * freezes while streaming NodeDB packets, the client would remain stuck in SYNCING_NODEDB.
   * This watchdog ensures that silence during sync triggers up to 3 automatic re-queries,
   * after which it gracefully promotes the session to CONNECTED if partial data was captured.
   */
  private resetSyncWatchdog(): void {
    this.stopSyncWatchdog();

    if (this.state !== 'SYNCING_NODEDB') return;

    this.syncWatchdogTimer = setTimeout(async () => {
      if (this.state !== 'SYNCING_NODEDB' || !this.transport || !this.transport.isConnected()) {
        return;
      }

      this.syncRetryCount++;
      console.warn(`[RadioClient] Sync watchdog timeout (${this.syncRetryCount}/3) for nonce ${this.configNonce}`);

      if (this.syncRetryCount <= 3) {
        if (this.configNonce === FULL_CONFIG_NONCE) {
          console.info('[RadioClient] Sync watchdog re-sending wantConfigId=69420 (Stage 1)...');
          await this.requestFullConfig();
        } else {
          console.info('[RadioClient] Sync watchdog re-sending wantConfigId=69421 (Stage 2)...');
          await this.requestNodeDbSync();
        }
      } else {
        // Exceeded retries: promote to CONNECTED so the user is not locked out indefinitely
        console.warn(`[RadioClient] Sync watchdog exceeded retries. Promoting to CONNECTED with ${this.syncedNodesCount} nodes.`);
        this.setState('CONNECTED');
        this.emit('syncProgress', this.syncedNodesCount, true);
      }
    }, 4000);
  }

  private stopSyncWatchdog(): void {
    if (this.syncWatchdogTimer !== null) {
      clearTimeout(this.syncWatchdogTimer);
      this.syncWatchdogTimer = null;
    }
  }

  private startHeartbeatLoop(): void {
    this.stopHeartbeatLoop();
    // Periodic heartbeat every 20 seconds resets firmware connection idle timers
    this.heartbeatTimer = setInterval(async () => {
      if (this.transport && this.transport.isConnected()) {
        try {
          await this.send(createHeartbeat(this.heartbeatNonce++));
        } catch {
          // Heartbeat non-blocking fail
        }
      }
    }, 20000);
  }

  private stopHeartbeatLoop(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  getState(): ConnectionState {
    return this.state;
  }

  getMyNodeInfo(): DecodedMyNodeInfo | null {
    return this.myNodeInfo;
  }

  getSyncedNodesCount(): number {
    return this.syncedNodesCount;
  }

  getTransportName(): string | undefined {
    return this.transport?.name;
  }

  on<K extends keyof RadioClientEvents>(event: K, handler: RadioClientEvents[K]): void {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event]!.push(handler);
  }

  private async handlePacketReceived(data: Uint8Array): Promise<void> {
    console.debug(`[RadioClient] Raw packet received: ${data.length} bytes [${Array.from(data.slice(0, 16)).map(b => b.toString(16).padStart(2, '0')).join(' ')}${data.length > 16 ? '...' : ''}]`);

    // Reset sync watchdog upon receiving any valid packet from the radio
    this.resetSyncWatchdog();

    try {
      const decoded: DecodedFromRadio = decodeFromRadio(data);

      // Log the decoded message type for debugging
      const fields = Object.keys(decoded).filter(k => decoded[k as keyof DecodedFromRadio] !== undefined && decoded[k as keyof DecodedFromRadio] !== null);
      console.debug(`[RadioClient] Decoded FromRadio fields: ${fields.join(', ')}`);

      if (decoded.rebooted) {
        console.warn('[RadioClient] Radio reported rebooted! Restarting config request...');
        this.syncedNodesCount = 0;
        await this.requestFullConfig();
        return;
      }

      if (decoded.myInfo) {
        console.info('[RadioClient] Received myInfo:', JSON.stringify(decoded.myInfo));
        this.myNodeInfo = decoded.myInfo;
        this.emit('myInfo', decoded.myInfo);
      }

      if (decoded.nodeInfo) {
        this.syncedNodesCount++;
        console.info(`[RadioClient] Received nodeInfo #${this.syncedNodesCount}: num=${decoded.nodeInfo.num}, user=${decoded.nodeInfo.user?.shortName || 'unknown'}`);
        this.emit('nodeInfo', decoded.nodeInfo);
        this.emit('syncProgress', this.syncedNodesCount, false);
      }

      if (decoded.fileInfo) {
        console.debug(`[RadioClient] Flash file manifest entry: ${decoded.fileInfo.fileName} (${decoded.fileInfo.sizeBytes} bytes)`);
      }

      if (decoded.configCompleteId !== undefined) {
        console.info(`[RadioClient] configCompleteId received: ${decoded.configCompleteId} (current configNonce: ${this.configNonce}), synced ${this.syncedNodesCount} nodes`);

        if (decoded.configCompleteId === FULL_CONFIG_NONCE && this.configNonce === FULL_CONFIG_NONCE) {
          // Stage 1 (Config, Channels, Files) complete!
          // Radio firmware now expects Stage 2: SPECIAL_NONCE_ONLY_NODES (69421) to stream the NodeDB.
          console.info('[RadioClient] Stage 1 (Config) complete. Initiating Stage 2: Streaming full NodeDB (nonce 69421)...');
          this.syncRetryCount = 0;
          await new Promise((r) => setTimeout(r, 100));
          await this.requestNodeDbSync();
        } else {
          // Stage 2 (NodeDB) complete (or direct NodeDB request completed)
          console.info(`[RadioClient] Stage 2 (NodeDB) complete! Successfully synced ${this.syncedNodesCount} nodes.`);
          this.stopSyncWatchdog();
          this.connectionReadyTimestamp = Math.floor(Date.now() / 1000);
          this.settleUntilTimestamp = Date.now() + 2500; // 2.5s settle window to drain all backlog ring-buffer flushes
          this.setState('CONNECTED');
          this.emit('syncProgress', this.syncedNodesCount, true);
        }
      }

      if (decoded.packet) {
        // 1. Discard packets received before the handshake and NodeDB sync are complete
        if (this.state !== 'CONNECTED') {
          console.debug('[RadioClient] Discarding packet received during handshake/sync state');
          return;
        }

        // 2. Discard packets flushed during the post-sync settle window (backlog ring-buffer flushes)
        if (Date.now() < this.settleUntilTimestamp) {
          console.debug(`[RadioClient] Discarding backlog packet during post-sync settle window (from=!${decoded.packet.from?.toString(16)})`);
          return;
        }

        // 3. Discard historical cached packets (rxTime was recorded in the past prior to connection)
        if (decoded.packet.rxTime && decoded.packet.rxTime > 0) {
          if (decoded.packet.rxTime < this.connectionReadyTimestamp - 5) {
            console.debug(`[RadioClient] Discarding historical packet (rxTime=${decoded.packet.rxTime} vs ready=${this.connectionReadyTimestamp})`);
            return;
          }
        }

        // 4. Discard packets generated by the local connected node itself
        const localNum = this.myNodeInfo?.myNodeNum;
        if (localNum !== undefined && (decoded.packet.from >>> 0) === (localNum >>> 0)) {
          console.debug(`[RadioClient] Discarding locally generated packet (from == !${localNum.toString(16)})`);
          return;
        }

        // 5. Must be a genuine over-the-air LoRa packet (has physical RF SNR measurement)
        if (decoded.packet.rxSnr === undefined || decoded.packet.viaMqtt) {
          console.debug(`[RadioClient] Discarding non-LoRa RF packet (rxSnr=${decoded.packet.rxSnr}, viaMqtt=${decoded.packet.viaMqtt})`);
          return;
        }

        // 6. Discard internal administrative packets (portnum 6: ADMIN_APP)
        if (decoded.packet.decoded?.portnum === 6) {
          console.debug('[RadioClient] Discarding administrative packet (portnum 6)');
          return;
        }

        console.debug(`[RadioClient] Live LoRa MeshPacket from=${decoded.packet.from?.toString(16)} to=${decoded.packet.to?.toString(16)} snr=${decoded.packet.rxSnr} dB`);
        this.emit('packet', decoded.packet);
      }

      // Log messages with only an id field (config/channel/module messages during sync — ignored)
      if (decoded.id && !decoded.packet && !decoded.myInfo && !decoded.nodeInfo && !decoded.fileInfo && decoded.configCompleteId === undefined && !decoded.rebooted) {
        console.debug(`[RadioClient] FromRadio id=${decoded.id} (config/channel/module message, no action needed)`);
      }
    } catch (err) {
      console.warn('[RadioClient] Failed to decode FromRadio packet:', err, 'Raw bytes:', Array.from(data.slice(0, 32)).map(b => b.toString(16).padStart(2, '0')).join(' '));
    }
  }

  private handleTransportDisconnect(): void {
    console.info('[RadioClient] Transport disconnected.');
    this.stopSyncWatchdog();
    this.stopHeartbeatLoop();

    if (this.isExplicitDisconnect) {
      this.setState('DISCONNECTED');
      return;
    }

    // Trigger auto-reconnect if unexpectedly disconnected
    if (this.currentTransportType) {
      this.scheduleAutoReconnect();
    } else {
      this.setState('DISCONNECTED');
    }
  }

  private handleTransportError(error: Error): void {
    console.error('[RadioClient] Transport error:', error);
    this.emit('error', error);
  }

  /**
   * Schedules an automatic reconnection attempt with exponential backoff and jitter.
   *
   * Why: When a radio node powers off, drops BLE connection, or reboots over USB, hammering
   * the OS Bluetooth/Serial stack with rapid immediate retries causes resource lockups.
   * Exponential backoff (2s, 3s, 4.5s... up to 30s) gives the radio MCU time to initialize
   * while keeping reconnection autonomous and hands-free for the vehicle driver.
   */
  private scheduleAutoReconnect(): void {
    this.cancelReconnectTimer();

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn(`[RadioClient] Reconnect failed after ${this.maxReconnectAttempts} attempts.`);
      this.reconnectAttempts = 0;
      this.setState('DISCONNECTED');
      this.emit('error', new Error(`Verbindung nach ${this.maxReconnectAttempts} Versuchen abgebrochen. Bitte Funkgerät prüfen.`));
      return;
    }

    this.reconnectAttempts++;
    this.setState('RECONNECTING');

    // Exponential backoff: base * 1.5^(attempts - 1), capped at max
    const delayMs = Math.min(
      Math.round(this.baseReconnectDelayMs * Math.pow(1.5, this.reconnectAttempts - 1)),
      this.maxReconnectDelayMs
    );

    console.info(`[RadioClient] Reconnect attempt #${this.reconnectAttempts}/${this.maxReconnectAttempts} scheduled in ${delayMs}ms...`);

    this.reconnectTimer = setTimeout(async () => {
      if (this.isExplicitDisconnect || this.state !== 'RECONNECTING' || !this.currentTransportType) {
        return;
      }

      try {
        console.info(`[RadioClient] Executing reconnect attempt #${this.reconnectAttempts}...`);

        if (this.transport && typeof this.transport.reconnect === 'function') {
          await this.transport.reconnect();
        } else if (this.transport) {
          await this.transport.connect();
        } else {
          this.transport = this.currentTransportType === 'serial' ? new SerialTransport() : new BleTransport();
          this.transport.onPacket(this.handlePacketReceived);
          this.transport.onError(this.handleTransportError);
          this.transport.onDisconnect(this.handleTransportDisconnect);

          if (typeof this.transport.reconnect === 'function') {
            await this.transport.reconnect();
          } else {
            await this.transport.connect();
          }
        }

        console.info('[RadioClient] Transport reconnected successfully! Resuming handshake...');
        this.reconnectAttempts = 0;
        this.setState('SYNCING_NODEDB');
        this.syncedNodesCount = 0;
        this.syncRetryCount = 0;
        this.startHeartbeatLoop();
        this.resetSyncWatchdog();

        await new Promise((r) => setTimeout(r, 200));
        await this.requestFullConfig();
      } catch (err) {
        console.warn(`[RadioClient] Reconnect attempt #${this.reconnectAttempts} failed:`, err);
        // Continue backoff loop
        this.scheduleAutoReconnect();
      }
    }, delayMs);
  }

  private cancelReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * Returns current count of automatic reconnection attempts.
   *
   * @returns Number of reconnection attempts made
   */
  getReconnectAttempts(): number {
    return this.reconnectAttempts;
  }

  /**
   * Returns configured maximum reconnection attempts.
   *
   * @returns Maximum retry attempts before giving up
   */
  getMaxReconnectAttempts(): number {
    return this.maxReconnectAttempts;
  }

  /**
   * Explicitly cancels active reconnection timer and restores disconnected state.
   *
   * Why: Allows operator to abort pending reconnection retries from modal dialog.
   */
  cancelReconnect(): void {
    this.cancelReconnectTimer();
    this.isExplicitDisconnect = true;
    this.reconnectAttempts = 0;
    this.setState('DISCONNECTED');
  }

  private setState(state: ConnectionState): void {
    this.state = state;
    this.emit('stateChange', state, this.transport?.name);
  }

  private emit<K extends keyof RadioClientEvents>(
    event: K,
    ...args: Parameters<RadioClientEvents[K]>
  ): void {
    const list = this.listeners[event];
    if (list) {
      for (const fn of list) {
        try {
          (fn as any)(...args);
        } catch (err) {
          console.error(`[RadioClient] Event listener error (${event}):`, err);
        }
      }
    }
  }
}
